import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useRouteMatch } from 'react-router-dom';
import { observer } from 'mobx-react';
import styled from 'styled-components/macro';

import { useDataStore } from 'state/DataStore';

import Page from 'components/Page';
import joinPath from 'utilities/joinPath';

import { ReactComponent as FeatureIconBed } from 'images/icons/feature-icon-bed-filled.svg';
import { ReactComponent as FeatureIconMpr } from 'images/icons/feature-icon-mpr-filled.svg';
import { ReactComponent as FeatureIconBath } from 'images/icons/feature-icon-bath-filled.svg';
import { ReactComponent as FeatureIconCar } from 'images/icons/feature-icon-car-filled.svg';


const Category = observer(() => {
    const dataStore = useDataStore();
    const match = useRouteMatch();
    const { categorySlug } = useParams();
    const { data: { unit_types } } = dataStore;
    
    const unit_category = dataStore.getItem('unit_categories', 'slug', categorySlug);
    
    const typesByCategory = unit_types.filter((type) => type.unit_categories.indexOf(unit_category.id) !== -1);
    
    typesByCategory.sort((a, b) => a.title?.rendered?.localeCompare(b.title?.rendered));

    return (
        <Page>
            <Container>
                {typesByCategory?.map(({ id, slug, title: { rendered: name }, acf: typeData }) => {
                    const isAvailable = dataStore.isUnitTypeAvailable(id);

                    const linkType = typeData?.floorplan ? Link : 'div';

                    return (
                        <TypeWrapper key={id}>
                            <Type as={linkType} to={joinPath(match.url, slug)}>
                                <TypePlan src={typeData?.floorplan?.sizes?.medium_large} />

                                {!isAvailable && (
                                    <SoldTag>SOLD OUT</SoldTag>
                                )}

                                <TypeInfo>
                                    <Name className="h6">Type {name}</Name>
                                    <Divider />
                                    <Features className="h6">
                                        <FeatureItem>
                                            <FeatureItemValue>{typeData.bedrooms}</FeatureItemValue>
                                            <FeatureIconBed />
                                        </FeatureItem>
                                        {typeData.mpr && (
                                            <FeatureItem>
                                                <FeatureItemValue>{typeData.mpr}</FeatureItemValue>
                                                <FeatureIconMpr />
                                            </FeatureItem>
                                        )}
                                        <FeatureItem>
                                            <FeatureItemValue>{typeData.bathrooms}</FeatureItemValue>
                                            <FeatureIconBath />
                                        </FeatureItem>
                                        <FeatureItem>
                                            <FeatureItemValue>{typeData.cars}</FeatureItemValue>
                                            <FeatureIconCar />
                                        </FeatureItem>
                                    </Features>
                                </TypeInfo>
                            </Type>
                        </TypeWrapper>
                    );
                })}
            </Container>
        </Page>
    );
});


export default Category;


const Container = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    column-gap: 5rem;
    row-gap: 4rem;

    &:has( :nth-child(11) ) {
        column-gap: 4rem;
    }

    &:not(:has( :nth-child(9) )) {
        max-width: 135rem;
    }

    &:not(:has( :nth-child(7) )) {
        max-width: 110rem;
    }

    &:not(:has( :nth-child(5) )) {
        max-width: 80rem;
    }
`;

const SoldTag = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 7rem;
    height: 7rem;
    padding: 0;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    border-radius: 50%;
    background-color: ${props => props.theme.sold_color};
    color: white;
`;

const TypePlan = styled.img`
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    object-position: center left;
    transition: transform 0.3s ease-in-out;

    &:not([src]) {
        opacity: 0;
    }
`;


const TypeWrapper = styled.div`
    width: 30rem;

    ${Container}:has( :nth-child(5) ) & { 
        width: 27rem;
    }

    ${Container}:has( :nth-child(11) ) & { 
        width: 24rem;
    }
`;

const Type = styled(Link)`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    color: ${props => props.theme.primary_color};
    
    &:is(a):hover ${TypePlan} {
        transform: scale(1.03);
    }
`;

const TypeInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Name = styled.div`
    line-height: 1;
`;

const Divider = styled.div`
    height: .1rem;
    width: 20rem;
    background-color: ${props => props.theme.secondary_color};
`;

const Features = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 1.5rem;
    color: currentColor;
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    line-height: 1;

    svg {
        height: 1em;
        width: auto;
    }
`;

const FeatureItemValue = styled.div`

`;
